import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import ContactSection from "@/components/ContactSection";
import SEO from "@/components/SEO";
import { Shield, ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import bannerImage from "@/assets/fitosanitari.jpg";

const Fitosanitari = () => {
  const products = [
    {
      name: "Rodenticidi",
      description: "Prodotti professionali per il controllo di topi e ratti",
      features: ["Esche fresche e blocchi", "Formulazioni anticoagulanti", "Prodotti per uso professionale"]
    },
    {
      name: "Insetticidi",
      description: "Soluzioni efficaci contro insetti volanti e striscianti",
      features: ["Spray e polveri", "Concentrati emulsionabili", "Prodotti per uso civile", "Trattamenti residuali"]
    },
    {
      name: "Protezione colture",
      description: "Fitofarmaci per la difesa delle coltivazioni",
      features: ["Fungicidi", "Insetticidi agricoli", "Diserbanti"]
    },
    {
      name: "Prodotti biologici",
      description: "Ampia gamma di prodotti per agricoltura biologica e convenzionale",
      features: ["Prodotti biologici certificati", "Fitofarmaci", "Soluzioni naturali", "Consulenza specializzata"]
    },
    {
      name: "Disinfestazione professionale",
      description: "Prodotti e attrezzature per operatori professionali - RICHIESTO PATENTINO",
      features: ["Attrezzature per trattamenti", "Consulenza tecnica", "Vendita solo con patentino fitosanitario"]
    }
  ];

  return (
    <div className="min-h-screen">
      <SEO
        title="Prodotti Fitosanitari e Disinfestanti - Magione"
        description="Prodotti fitosanitari, rodenticidi, insetticidi, fungicidi e diserbanti. Soluzioni biologiche e professionali con consulenza. A Magione (PG)."
        keywords="fitosanitari magione, rodenticidi perugia, insetticidi trasimeno, disinfestanti umbria, fitofarmaci biologici"
        canonical="/prodotti/fitosanitari"
        type="product"
      />
      <Navbar />

      {/* Hero Banner Section */}
      <section className="relative h-[450px] md:h-[500px] overflow-hidden pt-24 md:pt-28">
        {/* Background Image */}
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${bannerImage})` }}
        />
        {/* Overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-black/70" />

        {/* Content */}
        <div className="relative container mx-auto px-4 h-full flex flex-col justify-start md:justify-center pt-2 md:pt-0">
          <Link to="/#prodotti" className="mb-6 w-fit">
            <Button variant="outline" className="bg-white/10 text-white border-white/30 hover:bg-white/20 backdrop-blur-sm">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Torna ai prodotti
            </Button>
          </Link>

          <div className="max-w-4xl">
            <div className="icon-wrapper mb-6 w-16 h-16 text-white hidden md:flex items-center justify-center">
              <Shield className="w-12 h-12" />
            </div>
            <h1 className="text-4xl md:text-5xl font-heading font-bold mb-6 text-white">
              Prodotti Fitosanitari
            </h1>
            <p className="text-xl text-white/90 max-w-3xl">
              Soluzioni professionali per la disinfestazione e la protezione delle colture.
              Prodotti certificati e consulenza specializzata.
            </p>
          </div>
        </div>
      </section>

      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 max-w-7xl mx-auto">
            {products.map((product, index) => (
              <div
                key={index}
                className={`bg-card rounded-2xl p-8 border border-border card-hover ${
                  product.name === "Disinfestazione professionale" ? "sm:col-span-2 lg:col-span-3 xl:col-span-4" : ""
                }`}
              >
                <h3 className="text-2xl font-heading font-semibold mb-4">
                  {product.name}
                </h3>
                <p className="text-muted-foreground mb-6">
                  {product.description}
                </p>
                <ul className={`space-y-2 ${
                  product.name === "Disinfestazione professionale" ? "sm:grid sm:grid-cols-3 sm:gap-x-8 sm:gap-y-2" : ""
                }`}>
                  {product.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className={`flex items-start ${
                      product.name === "Disinfestazione professionale" ? "whitespace-nowrap" : ""
                    }`}>
                      <span className="text-primary mr-2 flex-shrink-0">✓</span>
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <div className="mt-12 p-6 bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 rounded-xl max-w-4xl mx-auto">
            <h3 className="text-xl font-semibold mb-2 text-amber-900 dark:text-amber-100">
              ⚠️ Uso responsabile
            </h3>
            <p className="text-amber-800 dark:text-amber-200">
              Tutti i prodotti fitosanitari devono essere utilizzati secondo le indicazioni riportate in etichetta.
              Il nostro personale è abilitato alla vendita e può fornire consulenza specializzata per un uso corretto e sicuro.
            </p>
          </div>
        </div>
      </section>

      <section className="py-16 bg-primary/5">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-heading font-bold mb-4">
            Consulenza specializzata
          </h2>
          <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
            Personale abilitato e qualificato per consigliarti il prodotto giusto
            e guidarti nell'utilizzo corretto e sicuro.
          </p>
          <Link
            to="/#contatti"
            className="inline-block px-8 py-3 bg-primary text-primary-foreground rounded-lg hover:opacity-90 transition-opacity"
          >
            Contattaci
          </Link>
        </div>
      </section>

      <ContactSection />
      <Footer />
    </div>
  );
};

export default Fitosanitari;
